import React from 'react';
import styled from 'styled-components';
import { theme } from '../../../styles/theme';
import { formatLevelDisplay, getLevelIcon } from '../../../utils/levelUtils';
import { fitText } from '../../../utils/sharePlatforms';

/**
 * Platform-shaped share cards (Release 14).
 * CSS sizes are half the export size; html2canvas captures at scale 2.
 *   story 540x960  -> 1080x1920 (Instagram / TikTok Stories)
 *   feed  540x675  -> 1080x1350 (4:5 feed post, native share, Save Image)
 *   link  600x315  -> 1200x630  (og:image for link previews)
 * Fixed sizes + JS truncation, because html2canvas has no line-clamp.
 */
export const FORMAT_SPECS = {
  story: { width: 540, height: 960, photo: { w: 476, h: 360 }, title: 90, reflection: 220, bg: theme.colors.deepNavy },
  feed: { width: 540, height: 675, photo: { w: 488, h: 220 }, title: 80, reflection: 190, bg: theme.colors.white },
  link: { width: 600, height: 315, photo: { w: 236, h: 267 }, title: 70, reflection: 100, bg: theme.colors.white },
};

export const SHARE_FORMATS = Object.keys(FORMAT_SPECS);

const formatDate = (timestamp) => {
  const d = timestamp?.toDate ? timestamp.toDate() : new Date();
  return d.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
};

// ─── Shared pieces ────────────────────────────────────────────────────────

const Frame = styled.div`
  width: ${p => p.$w}px;
  height: ${p => p.$h}px;
  background: ${p => p.$bg};
  box-sizing: border-box;
  overflow: hidden;
  position: relative;
  font-family: ${theme.typography.bodyFont};
  display: flex;
  flex-direction: ${p => (p.$row ? 'row' : 'column')};
`;

const PhotoSlot = styled.div`
  width: ${p => p.$w}px;
  height: ${p => p.$h}px;
  flex: 0 0 auto;
  border-radius: ${p => p.$radius || 12}px;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${p => p.$bg};
`;

const Pill = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  border-radius: 999px;
  font-weight: 600;
  font-size: ${p => p.$size || 13}px;
  padding: ${p => (p.$small ? '4px 10px' : '6px 14px')};
  background: ${p => p.$bg};
  color: ${p => p.$fg};
  border: ${p => p.$border || 'none'};
  white-space: nowrap;
`;

const PillIcon = styled.img`
  width: ${p => p.$size || 18}px;
  height: ${p => p.$size || 18}px;
  object-fit: contain;
`;

const Photo = ({ photo, activity, spec, bg, radius }) => {
  const src = photo?.dataUrl || activity.imageUrl || activity.thumbnailUrl;
  return (
    <PhotoSlot $w={spec.photo.w} $h={spec.photo.h} $bg={bg} $radius={radius}>
      {src ? (
        <img
          src={src}
          alt=""
          style={photo
            ? { width: photo.width, height: photo.height, display: 'block' }
            : { maxWidth: '100%', maxHeight: '100%', objectFit: 'contain', display: 'block' }}
        />
      ) : (
        <img
          src={activity.cause?.icon || '/icons/icon_activity.png'}
          alt=""
          style={{ width: 96, height: 96, objectFit: 'contain', opacity: 0.6 }}
        />
      )}
    </PhotoSlot>
  );
};

const Badges = ({ activity, dark, small }) => {
  const level = activity.cause?.level;
  const size = small ? 11 : 13;
  const icon = small ? 14 : 18;
  return (
    <div style={{ display: 'flex', gap: small ? 6 : 10, flexWrap: 'wrap' }}>
      {activity.cause?.title && (
        <Pill $bg={theme.colors.brandYellow} $fg={theme.colors.deepNavy} $size={size} $small={small}>
          {activity.cause.icon && <PillIcon src={activity.cause.icon} alt="" $size={icon} />}
          {activity.cause.title}
        </Pill>
      )}
      {level && (
        <Pill
          $bg={dark ? 'transparent' : theme.colors.deepNavy}
          $fg={theme.colors.white}
          $border={dark ? '1.5px solid rgba(255,255,255,0.7)' : 'none'}
          $size={size}
          $small={small}
        >
          {getLevelIcon(level) && <PillIcon src={getLevelIcon(level)} alt="" $size={icon} />}
          {formatLevelDisplay(level)}
        </Pill>
      )}
    </div>
  );
};

const Brand = ({ dark, size = 36, fontSize = 22 }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
    <img src="/logo192.png" alt="" style={{ width: size, height: size, objectFit: 'contain' }} />
    <span style={{
      fontFamily: theme.typography.headingFont, fontWeight: 700, fontSize,
      color: dark ? theme.colors.white : theme.colors.deepNavy,
    }}>
      Lightway
    </span>
  </div>
);

// ─── Story 9:16 ───────────────────────────────────────────────────────────

const StoryCard = React.forwardRef(({ activity, userName, photo }, ref) => {
  const spec = FORMAT_SPECS.story;
  return (
    <Frame ref={ref} $w={spec.width} $h={spec.height} $bg={spec.bg} style={{ padding: '60px 32px 72px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <Brand dark />
        {userName && <span style={{ color: '#C9C9D6', fontSize: 15 }}>{userName}</span>}
      </div>
      <h2 style={{
        fontFamily: theme.typography.headingFont, fontSize: 30, lineHeight: 1.2, fontWeight: 700,
        color: theme.colors.white, margin: '0 0 20px',
      }}>
        {fitText(activity.title, spec.title)}
      </h2>
      <Photo photo={photo} activity={activity} spec={spec} bg="rgba(255,255,255,0.08)" radius={16} />
      <div style={{ margin: '20px 0 16px' }}>
        <Badges activity={activity} dark />
      </div>
      {activity.reflection && (
        <p style={{
          margin: 0, padding: '4px 0 4px 16px', borderLeft: `4px solid ${theme.colors.brandYellow}`,
          color: theme.colors.white, fontStyle: 'italic', fontSize: 17, lineHeight: 1.5,
          whiteSpace: 'pre-wrap', wordWrap: 'break-word',
        }}>
          "{fitText(activity.reflection, spec.reflection)}"
        </p>
      )}
      <div style={{ marginTop: 'auto', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end' }}>
        <div>
          <div style={{ color: theme.colors.white, fontWeight: 600, fontSize: 16 }}>Join me on Lightway</div>
          <div style={{ color: theme.colors.brandYellow, fontWeight: 600, fontSize: 14 }}>lightway-app.lightandcover.org</div>
        </div>
        <div style={{ color: '#C9C9D6', fontSize: 13 }}>{formatDate(activity.timestamp)}</div>
      </div>
    </Frame>
  );
});
StoryCard.displayName = 'StoryCard';

// ─── Feed 4:5 ─────────────────────────────────────────────────────────────

const FeedCard = React.forwardRef(({ activity, userName, photo }, ref) => {
  const spec = FORMAT_SPECS.feed;
  return (
    <Frame
      ref={ref} $w={spec.width} $h={spec.height} $bg={spec.bg}
      style={{ padding: 24, border: `2px solid ${theme.colors.brandYellow}` }}
    >
      <div style={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        paddingBottom: 12, marginBottom: 14, borderBottom: `2px solid ${theme.colors.softGray}`,
      }}>
        <Brand />
        {userName && <span style={{ color: theme.colors.slateGray, fontSize: 14 }}>{userName}</span>}
      </div>
      <h2 style={{
        fontFamily: theme.typography.headingFont, fontSize: 24, lineHeight: 1.25, fontWeight: 700,
        color: theme.colors.deepNavy, margin: '0 0 14px',
      }}>
        {fitText(activity.title, spec.title)}
      </h2>
      <Photo photo={photo} activity={activity} spec={spec} bg={theme.colors.background} />
      <div style={{ margin: '14px 0 12px' }}>
        <Badges activity={activity} />
      </div>
      {activity.reflection && (
        <p style={{
          margin: 0, padding: '10px 14px', background: 'rgba(255, 209, 0, 0.1)', borderRadius: 8,
          borderLeft: `4px solid ${theme.colors.brandYellow}`, color: theme.colors.deepNavy,
          fontStyle: 'italic', fontSize: 15, lineHeight: 1.5, whiteSpace: 'pre-wrap', wordWrap: 'break-word',
        }}>
          "{fitText(activity.reflection, spec.reflection)}"
        </p>
      )}
      <div style={{
        marginTop: 'auto', paddingTop: 10, display: 'flex', justifyContent: 'space-between',
        color: theme.colors.slateGray, fontSize: 13,
      }}>
        <span style={{ fontWeight: 600 }}>lightway-app.lightandcover.org</span>
        <span>{formatDate(activity.timestamp)}</span>
      </div>
    </Frame>
  );
});
FeedCard.displayName = 'FeedCard';

// ─── Link 1.91:1 ──────────────────────────────────────────────────────────

const LinkCard = React.forwardRef(({ activity, userName, photo }, ref) => {
  const spec = FORMAT_SPECS.link;
  return (
    <Frame
      ref={ref} $w={spec.width} $h={spec.height} $bg={spec.bg} $row
      style={{ padding: 24, gap: 20, borderBottom: `6px solid ${theme.colors.brandYellow}` }}
    >
      <Photo photo={photo} activity={activity} spec={spec} bg={theme.colors.background} />
      <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
          <Brand size={26} fontSize={17} />
          {userName && <span style={{ color: theme.colors.slateGray, fontSize: 11 }}>{fitText(userName, 22)}</span>}
        </div>
        <h2 style={{
          fontFamily: theme.typography.headingFont, fontSize: 19, lineHeight: 1.25, fontWeight: 700,
          color: theme.colors.deepNavy, margin: '0 0 10px',
        }}>
          {fitText(activity.title, spec.title)}
        </h2>
        <Badges activity={activity} small />
        {activity.reflection && (
          <p style={{
            margin: '10px 0 0', color: theme.colors.deepNavy, fontStyle: 'italic', fontSize: 13,
            lineHeight: 1.45, whiteSpace: 'pre-wrap', wordWrap: 'break-word',
          }}>
            "{fitText(activity.reflection.replace(/\s*\n+\s*/g, ' '), spec.reflection)}"
          </p>
        )}
        <div style={{ marginTop: 'auto', color: theme.colors.slateGray, fontSize: 11, fontWeight: 600 }}>
          lightway-app.lightandcover.org
        </div>
      </div>
    </Frame>
  );
});
LinkCard.displayName = 'LinkCard';

const FormatCard = React.forwardRef(({ format, ...props }, ref) => {
  if (format === 'story') return <StoryCard ref={ref} {...props} />;
  if (format === 'link') return <LinkCard ref={ref} {...props} />;
  return <FeedCard ref={ref} {...props} />;
});
FormatCard.displayName = 'FormatCard';

export default FormatCard;
